import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CONDITION_ITEMS, CONDITION_OPERATORS } from 'containers/Inventory/constants';
import { FILTER_FLYOUT_DEFAULT_VALUES } from 'containers/Inventory/constants';

import {
  AdvancedInventoryFilterType,
  InventoryFilterType,
  InventoryGroupTypeOperator,
} from 'constants/enums';
import {
  InventoryConditionFilter,
  InventoryConditionItemOption,
  InventoryConditionOperatorOption,
  InventoryFilter,
  InventoryTreeNode,
} from 'types/customInventoryFilters.interface';
import { FilterFlyoutState } from 'types/inventory.interface';
import { countAdvancedFilters, countSimpleFilters } from 'utils/inventory';

interface InventoryFilterContextProps {
  filterFlyoutState: FilterFlyoutState;
  rootNode: InventoryTreeNode;
  inventoryFilters: object | null;
  conditionOperatorsMap: { [key: string]: InventoryConditionOperatorOption };
  conditionItemsMap: { [key: string]: InventoryConditionItemOption };
  workingSimpleFilters: Record<string, InventoryConditionFilter>;
  activeFiltersCount: number;
  selectedFiltersCount: number;
  setRootNode: (node: InventoryTreeNode) => void;
  onFiltersClick: () => void;
  onClearFiltersClick: () => void;
  handleFilterClose: () => void;
  handleApplyFilters: () => void;
  onSwitchFilterClick: () => void;
  setWorkingSimpleFilters: (filters: Record<string, InventoryConditionFilter>) => void;
  resetWorkingFilters: () => void;
}

const InventoryFilterContext = createContext<InventoryFilterContextProps | null>(null);

interface InventoryFilterProviderProps {
  children: ReactNode;
}

export const InventoryFilterProvider: FC<InventoryFilterProviderProps> = ({ children }) => {
  const [filterFlyoutState, setFilterFlyoutState] = useState<FilterFlyoutState>(FILTER_FLYOUT_DEFAULT_VALUES);
  const [rootNode, setRootNode] = useState<InventoryTreeNode>({
    id: uuidv4(),
    type: AdvancedInventoryFilterType.GROUP,
    groupOperator: InventoryGroupTypeOperator.AND,
    children: [],
  });
  const [advancedFilters, setAdvancedFilters] = useState<InventoryTreeNode>({
    id: uuidv4(),
    type: AdvancedInventoryFilterType.GROUP,
    groupOperator: InventoryGroupTypeOperator.AND,
    children: [],
  });
  const [simpleFilters, setSimpleFilters] = useState<Record<string, InventoryConditionFilter>>({});
  const [workingSimpleFilters, setWorkingSimpleFilters] = useState<Record<string, InventoryConditionFilter>>({});

  const [inventoryFilters, setInventoryFilters] = useState<object | null>(null);

  const activeFiltersCount = useMemo(() => {
    if (!inventoryFilters) {
      return 0;
    }

    let count = 0;

    if (filterFlyoutState.type === InventoryFilterType.SIMPLE) {
      Object.entries(inventoryFilters).forEach(([key, filter]) => {
        if (key === 'filters' && Array.isArray(filter)) {
          count += countSimpleFilters(filter);
        }
      });
    } else if (filterFlyoutState.type === InventoryFilterType.ADVANCED) {
      count = countAdvancedFilters(advancedFilters);
    }

    return count;
  }, [inventoryFilters, filterFlyoutState.type, advancedFilters]);

  const selectedFiltersCount = useMemo(() => {
    if (filterFlyoutState.type === InventoryFilterType.SIMPLE) {
      if (Object.keys(workingSimpleFilters).length === 0) {
        return 0;
      }

      return countSimpleFilters(Object.values(workingSimpleFilters));
    }

    if (filterFlyoutState.type === InventoryFilterType.ADVANCED) {
      return countAdvancedFilters(rootNode);
    }

    return 0;
  }, [workingSimpleFilters, filterFlyoutState.type, rootNode]);

  const conditionOperatorsMap = useMemo(() => CONDITION_OPERATORS.reduce((
    acc: { [key: string]: InventoryConditionOperatorOption },
    operator: InventoryConditionOperatorOption,
  ) => {
    acc[operator.key] = operator;
    return acc;
  }, {}), []);

  const conditionItemsMap = useMemo(() => CONDITION_ITEMS.reduce((
    acc: { [key: string]: InventoryConditionItemOption },
    item: InventoryConditionItemOption,
  ) => {
    acc[item.key] = item;
    return acc;
  }, {}), []);

  const onFiltersClick = () => {
    if (filterFlyoutState.type === null) {
      setFilterFlyoutState({
        type: InventoryFilterType.SIMPLE,
        open: true,
      });
      setWorkingSimpleFilters(simpleFilters);
    } else {
      setFilterFlyoutState((prev) => ({ ...prev, open: true }));

      if (filterFlyoutState.type === InventoryFilterType.ADVANCED) {
        setRootNode(advancedFilters);
      } else {
        setWorkingSimpleFilters(simpleFilters);
      }
    }
  };

  const onSwitchFilterClick = () => {
    setInventoryFilters(null);

    if (filterFlyoutState.type === InventoryFilterType.ADVANCED) {
      clearWorkingAdvancedFilters();
      setWorkingSimpleFilters({});
      setSimpleFilters({});
      setFilterFlyoutState((prev) => ({ ...prev, type: InventoryFilterType.SIMPLE }));
    } else if (filterFlyoutState.type === InventoryFilterType.SIMPLE) {
      clearWorkingSimpleFilters();
      setRootNode({
        id: uuidv4(),
        type: AdvancedInventoryFilterType.GROUP,
        groupOperator: InventoryGroupTypeOperator.AND,
        children: [],
      });
      setFilterFlyoutState((prev) => ({ ...prev, type: InventoryFilterType.ADVANCED }));
    }
  };

  const onClearFiltersClick = () => {
    if (filterFlyoutState.type === InventoryFilterType.ADVANCED) {
      clearWorkingAdvancedFilters();
    } else if (filterFlyoutState.type === InventoryFilterType.SIMPLE) {
      clearWorkingSimpleFilters();
    }
  };

  const handleFilterClose = () => {
    setFilterFlyoutState((prev) => ({ ...prev, open: false }));
    setWorkingSimpleFilters(simpleFilters);
  };

  const getFormattedAdvancedFilters = (node: InventoryTreeNode): InventoryFilter | null => {
    if (node.type === AdvancedInventoryFilterType.CONDITION) {
      return {
        field: node.conditionItem?.key,
        operator: node.conditionOperator?.key,
        value: Array.isArray(node.conditionValue) && node.conditionValue.length === 1
          ? node.conditionValue[0]
          : node.conditionValue,
      };
    }

    if (node.type === AdvancedInventoryFilterType.GROUP) {
      const childFilters = (node.children || [])
        .map(getFormattedAdvancedFilters)
        .filter((filter): filter is InventoryFilter => filter !== null);

      if (childFilters.length === 0) return null;

      return {
        logic: node.groupOperator,
        filters: childFilters,
      };
    }

    return null;
  };

  const handleApplyFilters = () => {
    let filters = null;
    if (filterFlyoutState.type === InventoryFilterType.ADVANCED) {
      filters = getFormattedAdvancedFilters(rootNode);
      setAdvancedFilters(rootNode);
    }

    if (filterFlyoutState.type === InventoryFilterType.SIMPLE) {
      filters = {
        logic: InventoryGroupTypeOperator.AND,
        filters: Object.values(workingSimpleFilters),
      };
      setSimpleFilters(workingSimpleFilters);
    }

    setInventoryFilters(filters);
    handleFilterClose();
  };

  const clearWorkingAdvancedFilters = () => {
    const defaultRootNode: InventoryTreeNode = {
      id: uuidv4(),
      type: AdvancedInventoryFilterType.GROUP,
      groupOperator: InventoryGroupTypeOperator.AND,
      children: [],
    };
    setRootNode(defaultRootNode);
  };

  const clearWorkingSimpleFilters = () => {
    setWorkingSimpleFilters({});
  };

  const resetWorkingFilters = () => {
    setWorkingSimpleFilters(simpleFilters);
  };

  const value = useMemo(() => ({
    filterFlyoutState,
    rootNode,
    inventoryFilters,
    conditionOperatorsMap,
    conditionItemsMap,
    workingSimpleFilters,
    activeFiltersCount,
    selectedFiltersCount,
    setRootNode,
    onFiltersClick,
    onClearFiltersClick,
    handleFilterClose,
    handleApplyFilters,
    onSwitchFilterClick,
    setWorkingSimpleFilters,
    resetWorkingFilters,
  }), [
    filterFlyoutState,
    rootNode,
    inventoryFilters,
    conditionOperatorsMap,
    conditionItemsMap,
    workingSimpleFilters,
    activeFiltersCount,
    selectedFiltersCount,
  ]);

  return (
    <InventoryFilterContext.Provider value={value}>
      {children}
    </InventoryFilterContext.Provider>
  );
};

export const useInventoryFilter = (): InventoryFilterContextProps => {
  const context = useContext(InventoryFilterContext);
  if (!context) {
    throw new Error('useInventoryFilters must be used within an InventoryFilterProvider');
  }
  return context;
};
